// Working-hours predicate for gating background polling. The office runs
// 07:00–17:00 Eastern, Mon–Fri; outside that window there's no one to serve
// fresh data to, so polling should pause (cuts free-tier Supabase egress from
// tabs left open overnight / over the weekend — 2026-06-11 egress mitigation).
//
// We gate on America/New_York wall-clock via Intl, NOT the device's local
// getHours()/getDay(): a laptop set to another timezone still gates on ET, and
// Intl handles the EST/EDT daylight-saving shift automatically.

const TZ = 'America/New_York';
const START_HOUR = 7; // inclusive — polling active from 07:00:00
const END_HOUR = 17; // exclusive — polling stops at 17:00:00

const WEEKEND = new Set(['Sat', 'Sun']);

// Constructed ONCE at module scope (not per call). A fixed-zone formatter still
// resolves EST vs EDT correctly per the date passed to formatToParts().
const etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  weekday: 'short',
  hour: 'numeric',
  hourCycle: 'h23',
});

/**
 * True when `now` falls within working hours: Mon–Fri, 07:00–17:00 ET.
 * `now` is injectable so the logic is unit-testable with fixed instants.
 */
export function isWithinWorkingHours(now: Date = new Date()): boolean {
  const parts = etFormatter.formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);

  if (WEEKEND.has(weekday)) return false;
  return hour >= START_HOUR && hour < END_HOUR;
}
